import PhoneField from '../PhoneField'
import EditorActions from 'components/Common/EditorActions'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { InputOrPicker } from 'components/Common/DateTimeField'
import { useFormik } from 'formik'
import { Link, Prompt } from 'react-router-dom'
import { Container, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import Breadcrumbs from 'components/Common/Breadcrumb'
import { store as socketStore } from 'components/Common/Socket/store'
import { get, post } from 'helpers/api_helper'
import { initialPlayerValues, validatePlayerCreation, buildPlayerCreationPayload, playerLevelOptions } from './playerCreationForm'
import { runPlayerCommand } from './playerCreationOperation'
import { PlayerCityPicker, OrganizerClubPicker } from './PlayerCreationChoices'
import './playerCreation.scss'
import { playerOperationKey, newPlayerOperationId, readPending, savePending, invitationLabels } from './playerInvitationState'

const usableSettings = options => ['activeSports', 'eligibleCountries', 'levels'].every(key => Array.isArray(options?.[key]) && options[key].length)

export function CreatePlayer({ settings = {}, userDetails = {}, history, location = {} }) {
  const { socket, socketClientId } = useContext(socketStore)?.state?.socket || {}
  const storageKey = playerOperationKey(userDetails.id || userDetails._id)
  const pending = useRef(readPending(storageKey))
  const request = useRef(null)
  const busy = useRef(false)
  const leaving = useRef(false)
  const recoveryStarted = useRef(false)
  const [status, setStatus] = useState(pending.current ? 'unknown' : 'idle')
  const [result, setResult] = useState(null)
  const [message, setMessage] = useState('')
  const [options, setOptions] = useState(settings || {})
  const [loading, setLoading] = useState(true)
  const [settingsError, setSettingsError] = useState('')
  const [retry, setRetry] = useState(0)
  const [choiceBusy, setChoiceBusy] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [exit, setExit] = useState(null)
  const locked = ['pending', 'unknown'].includes(status)
  const returnLocation = { pathname: '/accounts/players', search: location.state?.playerListSearch || '',
    state: { playerListContext: location.state?.playerListContext } }
  useEffect(() => {
    let current = true
    setLoading(true); setSettingsError('')
    get('/settings').then(response => {
      if (!usableSettings(response?.data)) throw new Error('Unavailable settings')
      if (current) setOptions(response.data)
    }).catch(() => { if (current) setSettingsError('Player options could not be loaded. Retry before creating the account.') })
      .finally(() => { if (current) setLoading(false) })
    return () => { current = false }
  }, [retry])
  const track = useCallback(async (attempt, resubmit = true) => {
    if (busy.current) return
    if (resubmit && attempt.payload) attempt = { ...attempt, correlationId: newPlayerOperationId() }
    busy.current = true; pending.current = attempt; savePending(storageKey, attempt)
    setStatus('pending'); setMessage('')
    request.current = runPlayerCommand({ ...attempt, socket,
      read: id => get('/admin/player-creations/' + id),
      submit: resubmit && attempt.payload ? () => post('/admin/player-creations', {
        ...attempt.payload, operationId: attempt.operationId, socketClientId,
      }, { headers: { 'X-Correlation-Id': attempt.correlationId } }) : undefined,
    })
    const outcome = await request.current.promise
    busy.current = false
    if (outcome.status === 'cancelled') return
    if (outcome.status === 'saved') {
      setResult({ ...outcome, operationId: attempt.operationId }); pending.current = null; savePending(storageKey, null)
    } else if (outcome.status === 'error') {
      setMessage(outcome.message); pending.current = null; savePending(storageKey, null)
    }
    setStatus(outcome.status)
  }, [socket, socketClientId, storageKey])
  useEffect(() => {
    if (pending.current && !recoveryStarted.current) { recoveryStarted.current = true; track(pending.current, false) }
  }, [track])
  useEffect(() => () => request.current?.cancel(), [])
  useEffect(() => {
    if (!result?.operationId || !['pending', 'sending'].includes(result.invitationStatus)) return undefined
    let active = true
    let timer
    const started = Date.now()
    const refresh = async () => {
      try {
        const response = await get('/admin/player-creations/' + result.operationId)
        const next = response?.data
        if (active && next?.status === 'saved' && next.id === result.id) {
          setResult(current => ({ ...current, ...next }))
          if (!['pending', 'sending'].includes(next.invitationStatus)) return
        }
      } catch { /* The saved account remains available during a network failure. */ }
      if (!active) return
      if (Date.now() - started >= 60000) setResult(current => ({ ...current, invitationStatus: 'unconfirmed' }))
      else timer = setTimeout(refresh, 2000)
    }
    timer = setTimeout(refresh, 2000)
    return () => { active = false; clearTimeout(timer) }
  }, [result?.id, result?.operationId, result?.invitationStatus])
  const form = useFormik({ initialValues: initialPlayerValues(), validate: values => validatePlayerCreation(values, options),
    onSubmit: values => {
      if (locked || busy.current || loading || settingsError || Object.values(choiceBusy).some(Boolean)) return
      return track({ ...(pending.current || { operationId: newPlayerOperationId(), correlationId: newPlayerOperationId() }), payload: buildPlayerCreationPayload(values) })
    },
  })
  const unsaved = status !== 'saved' && (form.dirty || locked)
  useEffect(() => {
    if (!unsaved) return undefined
    const beforeUnload = event => { event.preventDefault(); event.returnValue = '' }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [unsaved])
  const submit = async event => {
    event.preventDefault()
    if (locked || busy.current) return
    setSubmitted(true)
    const errors = await form.validateForm()
    await form.setTouched(Object.fromEntries(Object.keys(form.values).map(key => [key, true])), false)
    if (Object.keys(errors).length) { document.getElementById('player-' + Object.keys(errors)[0])?.focus(); return }
    await form.submitForm()
  }
  const field = (name, label, { required = false, type = 'text', choices, hint, ...props } = {}) => {
    const error = (submitted || form.touched[name]) && form.errors[name]
    if (type === 'tel') return <PhoneField key={name} id={'player-' + name} name={name} label={label} value={form.values[name]}
      disabled={locked} error={error} onBlur={form.handleBlur}
      onChange={value => { form.setFieldTouched(name, true, false); form.setFieldValue(name, value) }} />
    const shared = { id: 'player-' + name, name, value: form.values[name], className: 'form-control',
      onBlur: form.handleBlur, onChange: form.handleChange, 'aria-required': required, 'aria-invalid': Boolean(error),
      'aria-describedby': 'player-' + name + '-help', ...props }
    return <div className="form-group mb-0" key={name}>
      <label htmlFor={shared.id}>{label}{required && <span aria-hidden="true"> *</span>}</label>
      {choices ? <select {...shared}><option value="">{required ? 'Select…' : 'Unknown / not provided'}</option>
        {choices.map(choice => <option key={choice.value ?? choice} value={choice.value ?? choice}>{choice.label ?? choice}</option>)}</select>
        : <InputOrPicker {...shared} type={type} />}
      <small id={shared.id + '-help'} className={error ? 'text-danger' : 'text-muted'}>{error || hint}</small>
    </div>
  }
  const country = (name, locationName, label) => field(name, label, { required: true,
    choices: (options.eligibleCountries || []).map(country => ({ value: country.name, label: country.name })),
    onChange: event => form.setValues(values => ({ ...values, [name]: event.target.value, [locationName]: null })),
  })
  const city = (name, countryName, label) => <PlayerCityPicker name={name} label={label}
    country={options.eligibleCountries?.find(country => country.name === form.values[countryName])} value={form.values[name]}
    onChange={value => form.setFieldValue(name, value)} onBusy={setChoiceBusy} locked={locked}
    error={submitted || form.touched[name] ? form.errors[name] : ''} />
  const section = (title, children) => <section className="card"><div className="card-body"><h5 className="mb-3">{title}</h5>{children}</div></section>
  const grid = children => <div className="player-creation-grid">{children}</div>
  return <div className="page-content"><Container fluid>
    <Breadcrumbs title="Users" path="/accounts/players" breadcrumbItem="Create user" />
    <div className="player-creation">
      <Prompt when={unsaved} message={(target, action) => {
        if (leaving.current) return true
        setExit({ target, action }); return false
      }} />
      <Modal isOpen={Boolean(exit)} toggle={() => setExit(null)}>
        <ModalHeader toggle={() => setExit(null)}>Leave player creation?</ModalHeader>
        <ModalBody>{locked ? 'Creation may still be in progress. You can check its status when you return.' : 'Your changes have not been saved.'}</ModalBody>
        <ModalFooter><button type="button" className="btn btn-light" onClick={() => setExit(null)}>Keep editing</button>
          <button type="button" className="btn btn-primary" onClick={() => {
            leaving.current = true; const target = exit; setExit(null)
            if (target.action === 'REPLACE') history.replace(target.target); else history.push(target.target)
          }}>Leave page</button></ModalFooter>
      </Modal>
      {status === 'saved' ? <section className="card"><div className="card-body" role="status">
        <h4>Player created</h4><p>{invitationLabels[result?.invitationStatus]}</p>
        {result?.indexStatus === 'failed' && <p className="text-warning">Search indexing is not confirmed. The account is saved and can be opened directly.</p>}
        <div className="d-flex flex-wrap player-result-actions"><Link className="btn btn-primary" to={'/accounts/players/' + result.id}>Open user profile</Link>
          <button className="btn btn-light" type="button" onClick={() => {
            setStatus('idle'); form.resetForm(); setSubmitted(false); setResult(null); setChoiceBusy({})
          }}>Create another player</button></div>
      </div></section> : <form onSubmit={submit} noValidate aria-label="Create player">
        <p className="text-muted">Create the player’s account and send an invitation to set their password. Fields marked * are required.</p>
        {loading && <p role="status">Loading player options…</p>}
        {settingsError && <div role="alert" className="alert alert-warning">{settingsError} <button type="button" className="btn btn-link p-0" onClick={() => setRetry(value => value + 1)}>Retry options</button></div>}
        {message && <div role="alert" className="alert alert-danger">{message}</div>}
        {locked && <div className="alert alert-info" role="status">{status === 'pending' ? 'Waiting for the saved account confirmation…' : 'The creation result is not confirmed. Check before creating another account.'}
          {status === 'unknown' && <><button type="button" className="btn btn-link" onClick={() => track(pending.current)}>Check creation status</button><button type="button" className="btn btn-link" onClick={() => setStatus('resuming')}>Re-enter original details</button></>}</div>}
        {status === 'resuming' && <p className="alert alert-info">Re-enter the original details to resume the same request. If the account is already saved, its existing result will be recovered.</p>}
        {submitted && Object.keys(form.errors).length > 0 && <div className="alert alert-danger" role="alert"><strong>Check the highlighted fields.</strong>
          <ul className="mb-0">{Object.entries(form.errors).map(([name, error]) => <li key={name}><button type="button" className="btn btn-link p-0 text-left" onClick={() => document.getElementById('player-' + name)?.focus()}>{error}</button></li>)}</ul></div>}
        <fieldset disabled={locked}>
          {section('Player details', grid(<>
            {field('firstName', 'First name', { required: true, autoComplete: 'given-name', maxLength: 100 })}
            {field('lastName', 'Last name', { required: true, autoComplete: 'family-name', maxLength: 100 })}
            {field('birthDate', 'Birth date', { required: true, type: 'date', max: new Date().toISOString().slice(0, 10) })}
            {field('gender', 'Gender', { required: true, choices: [{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }] })}
            {country('country', 'location', 'Country')}{city('location', 'country', 'City')}
            {field('phone', 'Phone (optional)', { type: 'tel', autoComplete: 'tel', hint: 'Include the country code, for example +40.' })}
            {field('dominantHand', 'Dominant hand (optional)', { choices: [{ value: 'right', label: 'Right' }, { value: 'left', label: 'Left' }] })}
            {field('tshirtSize', 'T-shirt size (optional)', { choices: ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'] })}
          </>))}
          {section('Sport & level', <><p className="text-muted">Leave a level unknown if the player has not been evaluated. Participation rules still apply.</p>{grid(<>
            {field('favoriteSport', 'Main sport', { required: true, choices: (options.activeSports || []).map(sport => ({ value: sport, label: sport.charAt(0).toUpperCase() + sport.slice(1) })),
              onChange: event => form.setValues(values => ({ ...values, favoriteSport: event.target.value, singles: '', doubles: '' })) })}
            {field('singles', 'Singles level', { choices: playerLevelOptions(options, form.values.favoriteSport).map(String), disabled: !form.values.favoriteSport || locked })}
            {field('doubles', 'Doubles level', { choices: playerLevelOptions(options, form.values.favoriteSport).map(String), disabled: !form.values.favoriteSport || locked })}
          </>)}</>)}
          {section('Account & invitation', <>{grid(<>{field('email', 'Email address', { required: true, type: 'email', autoComplete: 'email', maxLength: 254,
            hint: 'The invitation will be sent to this address.' })}<div className="player-account-note"><strong>Pending · Free membership</strong>
              <p className="text-muted mb-0">The player activates the account by setting a password. Add a photo, premium membership or communication preferences from the profile after creation.</p></div></>)}
            <p className="text-muted mt-3 mb-0">Newsletter subscription is managed by the player.</p></>)}
          <section className="card"><div className="card-body"><details open={form.values.isOrganizer}>
            <summary>Organizer <span className="text-muted">Optional</span></summary>
            <label className="player-organizer-check"><input type="checkbox" name="isOrganizer" checked={form.values.isOrganizer}
              onChange={event => { form.setFieldValue('isOrganizer', event.target.checked); setChoiceBusy({}) }} /> Enable organizer details</label>
            {form.values.isOrganizer && grid(<>
              {field('organizerClientType', 'Organizer type', { required: true, choices: [{ value: 'physicalPerson', label: 'Individual' }, { value: 'company', label: 'Company' }] })}
              {field('organizerOSAccess', 'SportyaOS access', { required: true, choices: [{ value: 'false', label: 'No access' }, { value: 'true', label: 'Grant access after activation' }] })}
              {form.values.organizerClientType === 'company' && <>{field('organizerCompany', 'Company name', { required: true, maxLength: 200 })}{field('organizerRegistrationNumber', 'Registration number', { required: true, maxLength: 100 })}</>}
              {field('organizerFirstName', 'Organizer first name', { required: true, maxLength: 100 })}{field('organizerLastName', 'Organizer last name', { required: true, maxLength: 100 })}
              {field('organizerEmail', 'Organizer email', { required: true, type: 'email', maxLength: 254 })}{field('organizerPhone', 'Organizer phone (optional)', { type: 'tel' })}
              {country('organizerCountry', 'organizerLocation', 'Organizer country')}{city('organizerLocation', 'organizerCountry', 'Organizer city')}
              <OrganizerClubPicker value={form.values.organizerClubs} onChange={value => form.setFieldValue('organizerClubs', value)} onBusy={setChoiceBusy} locked={locked} />
            </>)}
          </details></div></section>
        </fieldset>
        <EditorActions dirty={form.dirty} pending={locked} saveLabel="Create player & send invitation" message="An invitation is sent automatically after the account is saved.">
          <button type="button" className="btn btn-light" onClick={() => history.push(returnLocation)}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={locked || loading || Boolean(settingsError) || !usableSettings(options) || Object.values(choiceBusy).some(Boolean)}>
            {status === 'pending' ? 'Creating player…' : 'Create player & send invitation'}</button>
        </EditorActions>
      </form>}
    </div>
  </Container></div>
}
